import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, X, Trash2, AlertTriangle, Upload, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface Cluster {
  id?: string;
  label: string;
  description: string;
  image_url?: string;
  order: number;
  subCategories: SubCategory[];
}

interface SubCategory {
  id?: string;
  label: string;
  description: string;
  order: number;
}

interface PollEditorProps {
  postId: string | null;
  published: boolean;
}

export const PollEditor = ({ postId, published }: PollEditorProps) => {
  const [pollId, setPollId] = useState<string | null>(null);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [hideZeroAllocated, setHideZeroAllocated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [responseCount, setResponseCount] = useState(0);

  useEffect(() => {
    if (postId) {
      loadPoll();
    }
  }, [postId]);

  const loadPoll = async () => {
    if (!postId) return;

    try {
      const { data: poll } = await supabase
        .from("polls")
        .select("*")
        .eq("blog_post_id", postId)
        .single();

      if (poll) {
        setPollId(poll.id);
        const settings = poll.settings as { hideZeroAllocated?: boolean } | null;
        setHideZeroAllocated(settings?.hideZeroAllocated || false);

        const { data: clustersData } = await supabase
          .from("poll_clusters")
          .select("*")
          .eq("poll_id", poll.id)
          .order("display_order");

        if (clustersData) {
          const clustersWithSubs = await Promise.all(
            clustersData.map(async (cluster) => {
              const { data: subs } = await supabase
                .from("poll_sub_categories")
                .select("*")
                .eq("cluster_id", cluster.id)
                .order("display_order");

              return {
                id: cluster.id,
                label: cluster.label,
                description: cluster.description || "",
                image_url: cluster.image_url || "",
                order: cluster.display_order,
                subCategories: subs?.map(sub => ({
                  id: sub.id,
                  label: sub.label,
                  description: sub.description || "",
                  order: sub.display_order
                })) || []
              };
            })
          );
          setClusters(clustersWithSubs);
        }

        // Get response count
        const { count } = await supabase
          .from("poll_responses")
          .select("*", { count: "exact", head: true })
          .eq("poll_id", poll.id);
        setResponseCount(count || 0);
      }
    } catch (error) {
      console.error("Error loading poll:", error);
    }
  };

  const addCluster = () => {
    if (clusters.length >= 10) {
      toast.error("Maximum 10 clusters allowed");
      return;
    }
    setClusters([...clusters, {
      label: "",
      description: "",
      image_url: "",
      order: clusters.length,
      subCategories: []
    }]);
  };

  const removeCluster = (index: number) => {
    setClusters(clusters.filter((_, i) => i !== index));
  };

  const updateCluster = (index: number, field: keyof Cluster, value: string) => {
    const updated = [...clusters];
    updated[index] = { ...updated[index], [field]: value };
    setClusters(updated);
  };

  const handleImageUpload = async (index: number, file: File) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;

      // Load image to check dimensions
      const img = await createImageBitmap(file);
      
      // If image width > 250px, create resized version
      if (img.width > 250) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        
        // Calculate dimensions maintaining aspect ratio
        const targetWidth = 200;
        const scaleFactor = targetWidth / img.width;
        canvas.width = targetWidth;
        canvas.height = img.height * scaleFactor;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Convert to blob
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => resolve(blob!), file.type);
        });
        
        // Upload original (for backup/full quality)
        const originalPath = fileName;
        await supabase.storage
          .from('poll-cluster-images')
          .upload(originalPath, file);
        
        // Upload small version
        const smallFileName = fileName.replace(/\.(\w+)$/, '-small.$1');
        const { error: uploadError } = await supabase.storage
          .from('poll-cluster-images')
          .upload(smallFileName, blob);
          
        if (uploadError) throw uploadError;
        
        // Get URL for small version
        const { data } = supabase.storage
          .from('poll-cluster-images')
          .getPublicUrl(smallFileName);
          
        updateCluster(index, 'image_url', data.publicUrl);
      } else {
        // Original is small enough, just upload as-is
        const { error: uploadError } = await supabase.storage
          .from('poll-cluster-images')
          .upload(fileName, file);
          
        if (uploadError) throw uploadError;
        
        const { data } = supabase.storage
          .from('poll-cluster-images')
          .getPublicUrl(fileName);
          
        updateCluster(index, 'image_url', data.publicUrl);
      }
      
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    }
  };

  const handleRemoveImage = async (index: number, imageUrl?: string) => {
    if (imageUrl) {
      try {
        const path = imageUrl.split('/').pop();
        if (path) {
          const filesToRemove = [path];
          
          // If this is the small version, also remove original
          if (path.includes('-small')) {
            const originalPath = path.replace('-small', '');
            filesToRemove.push(originalPath);
          } else {
            // If this is the original, also remove small version
            const smallPath = path.replace(/\.(\w+)$/, '-small.$1');
            filesToRemove.push(smallPath);
          }
          
          await supabase.storage
            .from('poll-cluster-images')
            .remove(filesToRemove);
        }
      } catch (error) {
        console.error("Error removing image:", error);
      }
    }
    updateCluster(index, 'image_url', '');
  };

  const addSubCategory = (clusterIndex: number) => {
    const cluster = clusters[clusterIndex];
    if (cluster.subCategories.length >= 5) {
      toast.error("Maximum 5 sub-categories allowed per cluster");
      return;
    }
    const updated = [...clusters];
    updated[clusterIndex].subCategories.push({
      label: "",
      description: "",
      order: cluster.subCategories.length
    });
    setClusters(updated);
  };

  const removeSubCategory = (clusterIndex: number, subIndex: number) => {
    const updated = [...clusters];
    updated[clusterIndex].subCategories = updated[clusterIndex].subCategories.filter((_, i) => i !== subIndex);
    setClusters(updated);
  };

  const updateSubCategory = (clusterIndex: number, subIndex: number, field: keyof SubCategory, value: string) => {
    const updated = [...clusters];
    updated[clusterIndex].subCategories[subIndex] = {
      ...updated[clusterIndex].subCategories[subIndex],
      [field]: value
    };
    setClusters(updated);
  };

  const savePoll = async () => {
    if (!postId) {
      toast.error("Cannot save poll without a post");
      return;
    }

    if (clusters.length === 0) {
      toast.error("Add at least one cluster");
      return;
    }

    if (clusters.some(c => !c.label.trim())) {
      toast.error("All clusters must have a label");
      return;
    }

    setLoading(true);
    try {
      let currentPollId = pollId;

      if (!currentPollId) {
        const { data: newPoll, error: pollError } = await supabase
          .from("polls")
          .insert({
            blog_post_id: postId,
            poll_type: "constant_sum_two_level",
            settings: { hideZeroAllocated }
          })
          .select()
          .single();

        if (pollError) throw pollError;
        currentPollId = newPoll.id;
        setPollId(currentPollId);
      } else {
        await supabase
          .from("polls")
          .update({ settings: { hideZeroAllocated } })
          .eq("id", currentPollId);

        await supabase.from("poll_clusters").delete().eq("poll_id", currentPollId);
      }

      for (const cluster of clusters) {
        const { data: newCluster, error: clusterError } = await supabase
          .from("poll_clusters")
          .insert({
            poll_id: currentPollId,
            label: cluster.label,
            description: cluster.description || null,
            image_url: cluster.image_url || null,
            display_order: cluster.order
          })
          .select()
          .single();

        if (clusterError) throw clusterError;

        if (cluster.subCategories.length > 0) {
          const subsToInsert = cluster.subCategories.map(sub => ({
            cluster_id: newCluster.id,
            label: sub.label,
            description: sub.description || null,
            display_order: sub.order
          }));

          const { error: subsError } = await supabase
            .from("poll_sub_categories")
            .insert(subsToInsert);

          if (subsError) throw subsError;
        }
      }

      toast.success("Poll saved successfully");
      await loadPoll();
    } catch (error) {
      console.error("Error saving poll:", error);
      toast.error("Failed to save poll");
    } finally {
      setLoading(false);
    }
  };

  const wipePollData = async () => {
    if (!pollId) return;

    setLoading(true);
    try {
      await supabase.from("polls").delete().eq("id", pollId);
      setPollId(null);
      setClusters([]);
      setResponseCount(0);
      toast.success("Poll data wiped successfully");
      setShowWipeDialog(false);
    } catch (error) {
      console.error("Error wiping poll:", error);
      toast.error("Failed to wipe poll data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Interactive Poll</span>
          <div className="flex items-center gap-2">
            {pollId && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setShowWipeDialog(true)}
                disabled={loading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Wipe Poll Data
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {published && responseCount > 0 && (
          <div className="p-4 bg-warning/10 border border-warning rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Published with {responseCount} responses</p>
              <p className="text-sm text-muted-foreground">Consider wiping poll data before republishing to reset results</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <Label htmlFor="hideZeroAllocated">Hide sub-categories for zero-allocated clusters</Label>
          <Switch
            id="hideZeroAllocated"
            checked={hideZeroAllocated}
            onCheckedChange={setHideZeroAllocated}
          />
        </div>

        <div className="space-y-4">
          {clusters.map((cluster, clusterIndex) => (
            <Card key={clusterIndex}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Cluster {clusterIndex + 1}</CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCluster(clusterIndex)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Label *</Label>
                  <Input
                    value={cluster.label}
                    onChange={(e) => updateCluster(clusterIndex, "label", e.target.value)}
                    placeholder="e.g., Technology"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={cluster.description}
                    onChange={(e) => updateCluster(clusterIndex, "description", e.target.value)}
                    placeholder="Optional description"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Image</Label>
                  {cluster.image_url ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 border rounded-lg">
                        <img 
                          src={cluster.image_url} 
                          alt={cluster.label}
                          className="w-16 h-16 object-cover rounded"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveImage(clusterIndex, cluster.image_url)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(clusterIndex, file);
                        }}
                        className="hidden"
                        id={`image-upload-${clusterIndex}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById(`image-upload-${clusterIndex}`)?.click()}
                        className="w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Image
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Sub-categories (0-5)</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addSubCategory(clusterIndex)}
                      disabled={cluster.subCategories.length >= 5}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Sub
                    </Button>
                  </div>

                  {cluster.subCategories.map((sub, subIndex) => (
                    <Card key={subIndex} className="p-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Sub {subIndex + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSubCategory(clusterIndex, subIndex)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                        <Input
                          value={sub.label}
                          onChange={(e) => updateSubCategory(clusterIndex, subIndex, "label", e.target.value)}
                          placeholder="Sub-category label"
                        />
                        <Textarea
                          value={sub.description}
                          onChange={(e) => updateSubCategory(clusterIndex, subIndex, "description", e.target.value)}
                          placeholder="Optional description"
                          rows={1}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={addCluster}
          disabled={clusters.length >= 10}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Cluster ({clusters.length}/10)
        </Button>

        <Button type="button" onClick={savePoll} disabled={loading} className="w-full">
          {loading ? "Saving..." : "Save Poll"}
        </Button>

        <AlertDialog open={showWipeDialog} onOpenChange={setShowWipeDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Wipe Poll Data?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the poll configuration and all {responseCount} responses. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={wipePollData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Wipe Data
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
